import { UserRole } from "@/core/services/api/enums/UserRole";
import { SystemOverviewSubpages } from "@/interfaces/admin/SystemOverviewSubpages";
import store from "@/store";
import { RouteLocationRaw } from "vue-router";
import { PortalService } from "@/core/services/api/interfaces/dict/PortalService";
import { Actions } from "@/store/enums/StoreEnums";
import { EntityRepresentation } from "@/core/services/api/interfaces/admin/TimelineModel";

const systemOverviewPages = Object.entries(SystemOverviewSubpages).map(
  ([subpage, title]) => {
    return {
      heading: (): string => title,
      route: {
        name: "admin.system.overview",
        params: { subpage },
      },
      roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
    };
  }
);

export interface GuardedElement<E> {
  roles?: string[];
  isAllowed?: () => boolean;
  sub?: E[] | (() => E[]);
}

export interface MainMenuElement extends GuardedElement<MainMenuElement> {
  heading: () => string;
  route?: RouteLocationRaw;
  onClick?: (event: Event) => void;
  isSection?: boolean;
  svgIcon?: string;
  fontIcon?: string;
  svgClass?: string;
}

export const MainMenuElements: MainMenuElement[] = [
  {
    heading: (): string => "Dashboard",
    route: "/admin",
    svgIcon: "/media/icons/duotune/general/gen001.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  },
  {
    heading: (): string => "(Default) White Label",
    isSection: true,
    svgIcon: "/media/icons/duotune/finance/fin008.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
    sub: () => [
      {
        heading: (): string => "(Default) Overview",
        route: {
          name: "admin.white-label.index",
        },
        svgIcon: "/media/icons/duotune/finance/fin008.svg",
        fontIcon: "bi-app-indicator",
        roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
      },
      {
        heading: (): string => "(Default) Documents",
        route: {
          name: "admin.white-label.documents",
        },
        svgIcon: "/media/icons/duotune/arrows/arr028.svg",
        fontIcon: "bi-app-indicator",
        roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
      },
    ],
  },
  {
    heading: (): string => "Users",
    route: "/admin/users",
    svgIcon: "/media/icons/duotune/communication/com014.svg",
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  },
  {
    heading: (): string => "Company List",
    route: "/admin/company-list",
    svgIcon: "/media/icons/duotune/ecommerce/ecm008.svg",
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  },
  {
    heading: (): string => "Settings",
    route: {
      name: "admin.settings",
    },
    svgIcon: "/media/icons/duotune/general/gen055.svg",
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  },
  {
    heading: (): string => "Auth Checkpoints",
    route: "/admin/auth-checkpoints",
    svgIcon: "/media/icons/duotune/general/gen019.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  },
  {
    heading: (): string => "Archives",
    isSection: true,
    svgIcon: "/media/icons/duotune/coding/cod010.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
    sub: () => [
      {
        heading: (): string => "KYC File Archives",
        route: {
          name: "admin.kyc-file-archives.list",
        },
        roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
      },
    ],
  },
  {
    heading: (): string => "Announcements",
    route: "/admin/announcements",
    svgIcon: "/media/icons/duotune/general/gen045.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  },
  {
    heading: (): string => "Activity Logs",
    route: "/admin/activity-logs",
    svgIcon: "/media/icons/duotune/general/gen020.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  },
  {
    heading: (): string => "Bank Accounts",
    route: "/admin/bank-accounts",
    isSection: true,
    svgIcon: "/media/icons/duotune/finance/fin004.svg",
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
    sub: () => [
      {
        heading: (): string => "Top-up balance",
        route: {
          name: "admin.transactions.credit",
        },
        roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
      },
      {
        heading: (): string => "Integrities",
        route: { name: "admin.bank-accounts.integrity" },
        roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
      },
    ],
  },
  {
    heading: (): string => "Transactions",
    route: "/admin/transactions",
    isSection: true,
    svgIcon: "/media/icons/duotune/finance/fin006.svg",
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
    sub: () => [
      {
        heading: (): string => "External Payment Approval",
        route: {
          name: "admin.transactions.list",
          params: { mode: "admin_action_require_transactions" },
        },
        isAllowed: (): boolean => store.getters.isRootNode,
        roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
      },
      {
        heading: (): string => "Approved",
        route: {
          name: "admin.transactions.list",
          params: { mode: "approved_transactions" },
        },
        roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
      },
      {
        heading: (): string => "Pending Review",
        route: {
          name: "admin.transactions.list",
          params: { mode: "pending_transactions" },
        },
        roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
      },
    ],
  },
  {
    heading: (): string => "Timelines",
    route: "/admin/timelines",
    isSection: true,
    svgIcon: "/media/icons/duotune/general/gen013.svg",
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
    sub: (): MainMenuElement[] => {
      return store.getters.getTimelineEntityClasses.map(
        (entityRepresentation: EntityRepresentation) => {
          return {
            heading: (): string => entityRepresentation.name.toUpperCase(),
            route: {
              name: "admin.timelines.index",
              params: {
                entity_class: entityRepresentation.slug,
              },
            },
            roles: [UserRole.ADMIN, UserRole.SUPER_ADMIN],
          };
        }
      );
    },
  },
  {
    heading: (): string => "System",
    isSection: true,
    route: "/admin/system",
    svgIcon: "/media/icons/duotune/finance/fin001.svg",
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
    sub: () => systemOverviewPages,
  },
  {
    heading: (): string => "Dashboard",
    route: "/manager",
    svgIcon: "/media/icons/duotune/general/gen001.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
    isAllowed: (): boolean =>
      !store.getters.isUserComplianceTrackingRequired &&
      store.getters.isUserCompanyEnabled &&
      store.getters.isIndividualOrHas2FA,
  },
  {
    heading: (): string => "Verification",
    route: { name: "profile.verifications" },
    svgIcon: "/media/icons/duotune/general/gen049.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
    isAllowed: (): boolean => store.getters.isUserComplianceTrackingRequired,
  },
  {
    heading: (): string => {
      if (store.getters.isThemeKatbit) {
        return "Assets";
      }

      return "Accounts";
    },
    route: "/manager/accounts",
    svgIcon: "/media/icons/duotune/finance/fin008.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
    isAllowed: (): boolean =>
      store.getters.isUserCompanyEnabled &&
      store.getters.isIndividualOrHas2FA &&
      !store.getters.isLimitedFeaturesFlexPsp,
  },
  {
    heading: (): string => "White Label",
    isSection: true,
    svgIcon: "/media/icons/duotune/finance/fin008.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
    isAllowed: (): boolean =>
      store.getters.isUserCompanyEnabled &&
      store.getters.isIndividualOrHas2FA &&
      !store.getters.isLimitedFeaturesFlexPsp &&
      store.getters.isCompanyWhiteLabelEnabled,
    sub: () => [
      {
        heading: (): string => "Overview",
        route: {
          name: "manager.white-label.index",
        },
        svgIcon: "/media/icons/duotune/finance/fin008.svg",
        fontIcon: "bi-app-indicator",
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.isIndividualOrHas2FA &&
          !store.getters.isLimitedFeaturesFlexPsp &&
          store.getters.isCompanyWhiteLabelEnabled,
      },
      {
        heading: (): string => "Documents",
        route: {
          name: "manager.white-label.documents",
        },
        svgIcon: "/media/icons/duotune/arrows/arr028.svg",
        fontIcon: "bi-app-indicator",
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.isIndividualOrHas2FA &&
          !store.getters.isLimitedFeaturesFlexPsp &&
          store.getters.isCompanyWhiteLabelEnabled,
      },
      {
        heading: (): string => "White-labeled: Accounts",
        route: "/white-labeled",
        isSection: true,
        svgIcon: "/media/icons/duotune/finance/fin003.svg",
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.isIndividualOrHas2FA &&
          store.getters.isCompanyWhiteLabelEnabled,
        sub: () => [
          {
            heading: (): string => "White-labeled: KYC",
            svgIcon: "/media/icons/duotune/technology/teh002.svg",
            route: {
              name: "manager.white-labeled.compliance-trackings",
            },
            roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
            isAllowed: (): boolean =>
              store.getters.isUserCompanyEnabled &&
              store.getters.isIndividualOrHas2FA &&
              store.getters.isCompanyWhiteLabelEnabled,
          },
          {
            heading: (): string => "White-labeled: Transactions",
            route: "/white-labeled/transactions",
            isSection: true,
            svgIcon: "/media/icons/duotune/finance/fin003.svg",
            roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
            isAllowed: (): boolean =>
              store.getters.isUserCompanyEnabled &&
              store.getters.isIndividualOrHas2FA &&
              store.getters.isCompanyWhiteLabelEnabled,
            sub: () => [
              {
                heading: (): string => "Completed",
                route: {
                  name: "manager.white-labeled.transactions",
                  params: { mode: "approved_transactions" },
                },
                roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
                isAllowed: (): boolean =>
                  store.getters.isUserCompanyEnabled &&
                  store.getters.isIndividualOrHas2FA &&
                  store.getters.isCompanyWhiteLabelEnabled,
              },
              {
                heading: (): string => "Pending",
                route: {
                  name: "manager.white-labeled.transactions",
                  params: { mode: "pending_transactions" },
                },
                roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
                isAllowed: (): boolean =>
                  store.getters.isUserCompanyEnabled &&
                  store.getters.isIndividualOrHas2FA &&
                  store.getters.isCompanyWhiteLabelEnabled,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    heading: (): string => "Crypto",
    route: "/manager/crypto",
    svgIcon: `/media/svg/coins/BTC.svg`,
    fontIcon: "bi-app-indicator",
    roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
    isAllowed: (): boolean =>
      store.getters.isUserCompanyEnabled &&
      store.getters.userHasTwoFactorAuth &&
      store.getters.isCompanyCryptoEnabled &&
      store.getters.isThemeIbanflex,
    sub: () => [
      {
        heading: (): string => "Sell",
        route: {
          name: "manager.crypto.sell",
        },
        svgIcon: "/media/icons/duotune/arrows/arr028.svg",
        fontIcon: "bi-app-indicator",
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.userHasTwoFactorAuth &&
          store.getters.isCompanyCryptoEnabled &&
          store.getters.isThemeIbanflex,
      },
      {
        heading: (): string => "Buy",
        route: {
          name: "manager.crypto.buy",
        },
        svgIcon: "/media/icons/duotune/arrows/arr025.svg",
        fontIcon: "bi-app-indicator",
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.userHasTwoFactorAuth &&
          store.getters.isCompanyCryptoEnabled &&
          store.getters.isThemeIbanflex,
      },
      {
        heading: (): string => "Withdraw",
        route: {
          name: "manager.crypto.withdraw",
        },
        svgIcon: "/media/icons/duotune/arrows/arr042.svg",
        fontIcon: "bi-app-indicator",
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.userHasTwoFactorAuth &&
          store.getters.isCompanyCryptoEnabled &&
          store.getters.isThemeIbanflex,
      },
      {
        heading: (): string => "Deposit",
        route: {
          name: "manager.crypto.deposit",
        },
        svgIcon: "/media/icons/duotune/arrows/arr065.svg",
        fontIcon: "bi-app-indicator",
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.userHasTwoFactorAuth &&
          store.getters.isCompanyCryptoEnabled &&
          store.getters.isThemeIbanflex,
      },
    ],
  },
  {
    heading: (): string => "Transactions",
    route: "/transactions",
    isSection: true,
    svgIcon: "/media/icons/duotune/finance/fin003.svg",
    roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
    isAllowed: (): boolean =>
      store.getters.isUserCompanyEnabled && store.getters.isIndividualOrHas2FA,
    sub: () => [
      {
        heading: (): string => "Beneficiaries",
        svgIcon: "/media/icons/duotune/communication/com014.svg",
        route: "/manager/transaction-agents",
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.isIndividualOrHas2FA,
      },
      {
        heading: (): string => "Completed Transactions",
        svgIcon: "/media/icons/duotune/general/gen043.svg",
        route: {
          name: "manager.transactions.list",
          params: { mode: "approved_transactions" },
        },
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.isIndividualOrHas2FA,
      },
      {
        heading: (): string => "Pending Transactions",
        route: {
          name: "manager.transactions.list",
          params: { mode: "pending_transactions" },
        },
        svgIcon: "/media/icons/duotune/general/gen012.svg",
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.isIndividualOrHas2FA,
      },
      {
        heading: (): string => "Withdraw",
        isSection: true,
        route: "/manager/transactions/create",
        svgIcon: "/media/icons/duotune/finance/fin003.svg",
        roles: [UserRole.MANAGER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.userHasTwoFactorAuth,
        sub: (): MainMenuElement[] => [
          {
            heading: (): string => "IBAN",
            route: {
              name: "manager.transactions.create",
              params: {
                action: "create",
                method: "iban",
              },
            },
            roles: [UserRole.MANAGER],
            isAllowed: (): boolean =>
              store.getters.isUserCompanyEnabled &&
              store.getters.userHasTwoFactorAuth,
          },
          /*{
            heading: (): string => "CRYPTO",
            route: {
              name: "manager.transactions.create",
              params: {
                action: "create",
                method: "crypto",
              },
            },
            roles: [UserRole.MANAGER],
            isAllowed: (): boolean =>
              store.getters.isUserCompanyEnabled &&
              store.getters.userHasTwoFactorAuth,
          },*/
          {
            heading: (): string => "TRANSFER",
            route: {
              name: "manager.transactions.create",
              params: {
                action: "create",
                method: "transfer",
              },
            },
            roles: [UserRole.MANAGER],
            isAllowed: (): boolean =>
              store.getters.isUserCompanyEnabled &&
              store.getters.userHasTwoFactorAuth &&
              !store.getters.isThemeKatbit,
          },
          {
            heading: (): string => "EXCHANGE",
            route: {
              name: "manager.transactions.create",
              params: {
                action: "create",
                method: "exchange",
              },
            },
            roles: [UserRole.MANAGER],
            isAllowed: (): boolean =>
              store.getters.isUserCompanyEnabled &&
              store.getters.userHasTwoFactorAuth &&
              !store.getters.isThemeKatbit,
          },
          {
            heading: (): string => "Convert Currencies",
            route: {
              name: "manager.transactions.create",
              params: {
                action: "create",
                method: "convert",
              },
            },
            roles: [UserRole.MANAGER],
            isAllowed: (): boolean =>
              store.getters.isUserCompanyEnabled &&
              store.getters.userHasTwoFactorAuth &&
              store.getters.isThemeKatbit,
          },
          {
            heading: (): string => "TRANSFER TO PORTAL",
            isSection: true,
            route: "/manager/transactions/create",
            roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
            isAllowed: (): boolean =>
              store.getters.isUserAuthenticated &&
              !store.getters.isUserComplianceTrackingRequired &&
              store.getters.isCompanyEnabled &&
              store.getters.isCompanyFeaturePortalsEnabled &&
              store.getters.getPortalServicesAttached.length,
            sub: (): MainMenuElement[] =>
              store.getters.getPortalServicesAttached.map(
                (portalService: PortalService) => {
                  return {
                    heading: (): string => portalService.name.toUpperCase(),
                    route: {
                      name: "manager.portal-services",
                      params: {
                        portal_service: portalService.value,
                      },
                    },
                    roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
                  };
                }
              ),
          },
          {
            heading: (): string => "IMPORT",
            route: {
              name: "manager.transactions.create",
              params: {
                action: "create",
                method: "import",
              },
            },
            roles: [UserRole.MANAGER],
            isAllowed: (): boolean =>
              store.getters.isUserCompanyEnabled &&
              store.getters.userHasTwoFactorAuth &&
              !store.getters.isThemeKatbit,
          },
        ],
      },
      {
        heading: (): string => "Reports",
        route: "/manager/reports",
        svgIcon: "/media/icons/duotune/files/fil017.svg",
        fontIcon: "bi-app-indicator",
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.userHasTwoFactorAuth &&
          store.getters.isThemeKatbit,
      },
      {
        heading: (): string => "KYC",
        route: "/manager/compliance-trackings",
        svgIcon: "/media/icons/duotune/technology/teh002.svg",
        fontIcon: "bi-app-indicator",
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.isIndividualOrHas2FA &&
          store.getters.isThemeKatbit,
      },
    ],
  },
  {
    heading: (): string => "Sub-accounts",
    route: "/affiliates",
    isSection: true,
    svgIcon: "/media/icons/duotune/finance/fin003.svg",
    roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
    isAllowed: (): boolean =>
      store.getters.isUserCompanyEnabled &&
      store.getters.isIndividualOrHas2FA &&
      store.getters.isCompanyFeatureAffiliationEnabled,
    sub: () => [
      {
        heading: (): string => "KYC",
        svgIcon: "/media/icons/duotune/technology/teh002.svg",
        route: {
          name: "manager.affiliates.compliance-trackings",
        },
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.isIndividualOrHas2FA,
      },
      {
        heading: (): string => "Transactions",
        route: "/affiliates/transactions",
        isSection: true,
        svgIcon: "/media/icons/duotune/finance/fin003.svg",
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.isIndividualOrHas2FA,
        sub: () => [
          {
            heading: (): string => "Completed Transactions",
            route: {
              name: "manager.affiliates.transactions",
              params: { mode: "approved_transactions" },
            },
            roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
            isAllowed: (): boolean =>
              store.getters.isUserCompanyEnabled &&
              store.getters.isIndividualOrHas2FA,
          },
          {
            heading: (): string => "Pending Transactions",
            route: {
              name: "manager.affiliates.transactions",
              params: { mode: "pending_transactions" },
            },
            roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
            isAllowed: (): boolean =>
              store.getters.isUserCompanyEnabled &&
              store.getters.isIndividualOrHas2FA,
          },
        ],
      },
    ],
  },
  {
    heading: (): string => "KYC",
    route: "/manager/compliance-trackings",
    svgIcon: "/media/icons/duotune/technology/teh002.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
    isAllowed: (): boolean =>
      store.getters.isUserCompanyEnabled &&
      store.getters.isIndividualOrHas2FA &&
      store.getters.isThemeDefault &&
      !store.getters.isLimitedFeaturesFlexPsp,
  },
  {
    heading: (): string => "Reports",
    route: "/manager/reports",
    svgIcon: "/media/icons/duotune/files/fil017.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
    isAllowed: (): boolean =>
      store.getters.isUserCompanyEnabled &&
      store.getters.userHasTwoFactorAuth &&
      store.getters.isThemeIbanflex,
  },
  {
    heading: (): string => "Imports",
    route: "/manager/imports",
    svgIcon: "/media/icons/duotune/files/fil018.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
    isAllowed: (): boolean =>
      store.getters.isUserCompanyEnabled &&
      store.getters.userHasTwoFactorAuth &&
      !store.getters.isThemeKatbit,
  },
  {
    heading: (): string => "Notifications",
    route: "/manager/notifications",
    svgIcon: "/media/icons/duotune/general/gen022.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
    isAllowed: (): boolean =>
      store.getters.isUserCompanyEnabled &&
      store.getters.isIndividualOrHas2FA &&
      store.getters.isThemeIbanflex &&
      !store.getters.isLimitedFeaturesFlexPsp,
  },
  {
    heading: (): string => "Support Center",
    isSection: true,
    route: "/manager/support",
    svgIcon: "/media/icons/duotune/communication/com003.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
    isAllowed: (): boolean =>
      store.getters.isUserCompanyEnabled && store.getters.isIndividualOrHas2FA,
    sub: () => [
      {
        heading: (): string => "Tickets",
        roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
        svgIcon: "/media/icons/duotune/communication/com003.svg",
        route: "/manager/support/tickets/list",
        isAllowed: (): boolean =>
          store.getters.isUserCompanyEnabled &&
          store.getters.isIndividualOrHas2FA,
      },
    ],
  },
  {
    heading: (): string => "Portal Services",
    isSection: true,
    route: "/manager/portal-services",
    svgIcon: "/media/icons/duotune/arrows/arr024.svg",
    fontIcon: "bi-app-indicator",
    roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
    isAllowed: (): boolean =>
      store.getters.isUserAuthenticated &&
      !store.getters.isUserComplianceTrackingRequired &&
      store.getters.isCompanyEnabled &&
      store.getters.isCompanyFeaturePortalsEnabled &&
      store.getters.getPortalServicesAttached.length,
    sub: () => {
      return store.getters.getPortalServicesAttached.map(
        (portalService: PortalService) => {
          return {
            heading: (): string =>
              `Login to: ${portalService.name.toUpperCase()}`,
            onClick: async () => {
              return store
                .dispatch(
                  Actions.MANAGER_PORTAL_SERVICES_SIGN_IN,
                  portalService
                )
                .catch(console.error);
            },
            isAllowed: (): boolean => true,
            roles: [UserRole.MANAGER, UserRole.MANAGER_READER],
          };
        }
      );
    },
  },
];
